import {CardContent, CardHeader, TextField, Typography, Box} from '@mui/material'
import jwt_decode from 'jwt-decode'

import {getToken} from '../helpers/ReduxHelpers'
import {useState, useCallback, useMemo} from 'react'
import {VisibilityTwoTone} from '@mui/icons-material'
import {postGeneric} from '../common/constants'
import {useSnackbar} from 'notistack'
import {LoadingButton} from '@mui/lab'
import MUIModal from './MuiModal'

const OTP_LENGTH = 6

export default function PIIDetails(props) {
  const {title, fallbackComponent, merchantOid, reference, awbNumber, isReverseShipment} = props

  const piiAccessAllow = useMemo(() => {
    try {
      return jwt_decode(getToken())?.piiAccessAllow
    } catch {
      return false
    }
  }, [])

  const [loading, setLoading] = useState(false)
  const [tokenId, setTokenId] = useState(null)
  const [otp, setOtp] = useState('')
  const [otpLoading, setOtpLoading] = useState(false)
  const [ippDetails, setIppDetails] = useState(null)

  const {enqueueSnackbar} = useSnackbar()

  const handleShowPii = useCallback(async () => {
    try {
      setLoading(true)
      const response = await postGeneric('orderdata/send-otp', {
        merchantOid,
        reference,
        awbNumber,
      })
      setTokenId(response?.data?.tokenId)
    } catch (error) {
      setTokenId(null)
      enqueueSnackbar(error?.response?.data?.message, {variant: 'error'})
    } finally {
      setLoading(false)
    }
  }, [merchantOid, reference, awbNumber, enqueueSnackbar])

  const handleOtpChange = useCallback((e) => {
    const value = e.target.value
    // Only allow numeric input and limit to 6 digits
    if (value === '' || (/^\d+$/.test(value) && value.length <= OTP_LENGTH)) {
      setOtp(value)
    }
  }, [])

  const handleOtpSubmit = useCallback(async () => {
    const trimmedOtp = otp.trim()

    if (!trimmedOtp) {
      enqueueSnackbar('Please enter OTP', {variant: 'error'})
      return
    }

    if (trimmedOtp.length !== OTP_LENGTH) {
      enqueueSnackbar(`OTP must be ${OTP_LENGTH} digits`, {variant: 'error'})
      return
    }

    if (!tokenId) {
      enqueueSnackbar('Token ID is missing', {variant: 'error'})
      return
    }

    try {
      setOtpLoading(true)
      const response = await postGeneric('orderdata/getdata', {
        merchantOid,
        reference,
        awbNumber,
        tokenId,
        otp: trimmedOtp,
      })

      if (response?.data) {
        setIppDetails({
          email: isReverseShipment ? response.data.pickupEmail : response.data.deliveryEmail,
          phone: isReverseShipment ? response.data.pickupPhone : response.data.deliveryPhone,
        })
        setTokenId(null)
        setOtp('')
        enqueueSnackbar('PII data retrieved successfully', {variant: 'success'})
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Failed to verify OTP'
      enqueueSnackbar(errorMessage, {variant: 'error'})
    } finally {
      setOtpLoading(false)
    }
  }, [otp, tokenId, merchantOid, reference, awbNumber, isReverseShipment, enqueueSnackbar])

  const handleCloseOtpModal = useCallback(() => {
    setTokenId(null)
    setOtp('')
  }, [])

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !otpLoading && otp.trim().length === OTP_LENGTH) {
        handleOtpSubmit()
      }
    },
    [otp, otpLoading, handleOtpSubmit]
  )

  const displayDetails = useMemo(() => {
    return ippDetails || {email: '**********', phone: '**********'}
  }, [ippDetails])

  return (
    <>
      <CardHeader
        title={title}
        action={
          piiAccessAllow && !ippDetails ? (
            <LoadingButton
              onClick={handleShowPii}
              loading={loading}
              color='error'
              size='small'
              variant='contained'
              startIcon={<VisibilityTwoTone />}
            >
              Show PII
            </LoadingButton>
          ) : null
        }
      />
      <CardContent>{fallbackComponent(displayDetails)}</CardContent>

      <MUIModal
        open={tokenId !== null}
        handleClose={handleCloseOtpModal}
        title='Enter OTP'
        maxWidth='sm'
        hideClose={false}
        action={
          <LoadingButton
            onClick={handleOtpSubmit}
            loading={otpLoading}
            variant='contained'
            color='primary'
            disabled={otp.trim().length !== OTP_LENGTH}
          >
            Verify OTP
          </LoadingButton>
        }
      >
        <Box sx={{p: 2}}>
          <Typography variant='body2' sx={{mb: 2}}>
            Please enter the {OTP_LENGTH}-digit OTP sent to your registered email/phone number to
            view PII details.
          </Typography>
          <TextField
            fullWidth
            label='OTP'
            value={otp}
            onChange={handleOtpChange}
            placeholder={`Enter ${OTP_LENGTH}-digit OTP`}
            variant='outlined'
            type='tel'
            inputMode='numeric'
            autoComplete='one-time-code'
            autoFocus
            maxLength={OTP_LENGTH}
            inputProps={{
              pattern: '[0-9]*',
              maxLength: OTP_LENGTH,
            }}
            onKeyDown={handleKeyDown}
            helperText={
              otp.length > 0 && otp.length < OTP_LENGTH
                ? `${OTP_LENGTH - otp.length} digit(s) remaining`
                : ''
            }
          />
        </Box>
      </MUIModal>
    </>
  )
}
